import React, { useState, useEffect } from 'react';
import { PageHeader } from '../../../PageHeader';
import { Button, Checkbox, TextField, FormControl } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { Site, SiteDto } from '../../../../dtos/site';
import { useApi } from '../../../useApi';
import { useRouterWithTypedParams } from '../../../../hooks/useRouterWithTypedParams';
import { AjaxResult } from '../../../../enums/ajaxResult';
import { useSnackbar } from 'notistack';
import { ValidationErrors } from '../../../shared/validationErrors';
import { FormField } from '../../../shared/formField';
import { TimeZone } from '../../../../enums/timeZone';
import { TimeZoneSelect } from '../TimeZoneSelect';

import { DEVICE_MANAGEMENT_LOGIN_HOST_PATH } from "../../../../constants";
import { useAuth } from "../../../../features/auth/context";
import { ConfigurationContainer } from "../../../shared/ConfigurationContainer";
import { DEFAULT_MAINTENANCE_WINDOW, EMPTY_MAINTENANCE_WINDOW } from "../consts";

import { MaintenanceWindowConfiguration } from "./MaintenanceWindowConfiguration";
import { DeviceActionsSection } from "./DeviceActionsSection";
import { SitePhotos } from "./SitePhotos";
import { EnableMaintenanceModeDialog } from "../EnableMaintenanceModeDialog";

export const SiteConfigPage = () => {
    const classes = useStyles();
    const { isGlobalAdmin, isDealerAdmin } = useAuth();
    const { enqueueSnackbar } = useSnackbar();
    const { match } = useRouterWithTypedParams<{ siteId?: number }>();
    const siteId = match.params.siteId;
    const { siteApi, maintenanceModeApi } = useApi();
    const [site, setSite] = useState<Site>();
    const [showEnableMaintenanceDialog, setShowEnableMaintenanceDialog] = useState(false);
    const [isInMaintenanceMode, setIsInMaintenanceMode] = useState(false);
    const [maintenanceModeRefreshTrigger, setMaintenanceModeRefreshTrigger] = useState(0);
    useEffect(() => {
        if (!siteId) {
            return;
        }
        siteApi.getById(siteId).then((r) => {
            if (r.result === AjaxResult.Success && r.data) {
                setSite(r.data);
            }
        });
    }, [setSite, siteApi, siteId]);

    useEffect(() => {
        if (!siteId) {
            return;
        }
        maintenanceModeApi.getStatus(siteId).then((r) => {
            if (r.result === AjaxResult.Success && r.data) {
                setIsInMaintenanceMode(r.data.isInMaintenanceMode);
            }
        });
    }, [siteId, maintenanceModeApi, maintenanceModeRefreshTrigger]);

    const [validationErrors, setValidationErrors] = useState<string[]>([]);
    const updateSite = () => {
        if (!siteId) {
            return;
        }
        siteApi.updateSite(siteId, { ...site } as SiteDto).then((r) => {
            if (r.result === AjaxResult.Success) {
                enqueueSnackbar("Successfully updated site configuration", { variant: "success" });
                setValidationErrors([]);
            } else if (r.result === AjaxResult.Duplicate) {
                enqueueSnackbar("Cannot use that Reference Site ID for this site, already used", { variant: "error" });
            } else if (r.messages) {
                setValidationErrors(r.messages);
            } else {
                enqueueSnackbar("Failed to update configuration for site", { variant: "error" });
            }
        });
    };

    const showDeviceActionsSection = isGlobalAdmin || isDealerAdmin;
    const showCommissioningSiteLink = isGlobalAdmin || isDealerAdmin;

    const loadSite = () => {
        if (!siteId) {
            return;
        }
        siteApi.getById(siteId).then((r) => {
            if (r.result === AjaxResult.Success && r.data) {
                setSite(r.data);
            }
        });
    };

    const handleMaintenanceModeSuccess = () => {
        loadSite();
        setMaintenanceModeRefreshTrigger(prev => prev + 1);
    };

    return (
        <>
            <PageHeader
                openUrlIconProps={
                    showCommissioningSiteLink && site && site.balenaCloudDeviceId
                        ? {
                            url: `https://${site.balenaCloudDeviceId}.${DEVICE_MANAGEMENT_LOGIN_HOST_PATH}`,
                            tooltip: "Open Commissioning Site in new tab"
                        }
                        : undefined
                }
            >
                Site Configuration: {site && site.name}
            </PageHeader>

            <ConfigurationContainer>
                <FormField label="Site Name">
                    <FormControl>
                        <TextField
                            value={site ? site.name : ''}
                            onChange={(event) => {
                                if (site !== undefined) {
                                    setSite({ ...site, name: event.currentTarget.value });
                                }
                            }}
                            InputProps={{
                                inputProps: { className: classes.input },
                            }}
                        />
                    </FormControl>
                </FormField>
                <FormField label="Reference Site ID">
                    <FormControl>
                        <TextField
                            value={site ? site.referenceSiteId : ''}
                            onChange={(event) => {
                                if (site !== undefined) {
                                    setSite({ ...site, referenceSiteId: event.currentTarget.value });
                                }
                            }}
                            InputProps={{
                                inputProps: { className: classes.input },
                            }}
                        />
                    </FormControl>
                </FormField>
                <FormField label="Time zone">
                    <FormControl>
                        <TimeZoneSelect
                            value={site ? site.timeZone as TimeZone : TimeZone.UTC}
                            onChange={(timeZone) => {
                                if (site === undefined) return;
                                setSite({ ...site, timeZone });
                            }}
                            inputProps={{ className: classes.input }}
                        />
                    </FormControl>
                </FormField>
                <FormField label="Demo Site">
                    <Checkbox
                        checked={site ? site.excludeFromBilling : false}
                        color={'primary'}
                        onChange={(event) => {
                            if (site !== undefined) {
                                setSite({ ...site, excludeFromBilling: event.target.checked });
                            }
                        }}
                        disabled={!isGlobalAdmin}
                    />
                </FormField>
                {site && (isGlobalAdmin || isDealerAdmin) && (
                    <FormField label="Automatic Session Abandonment">
                        <Checkbox
                            checked={site && site.siteConfiguration ? site.siteConfiguration.automaticSessionAbandonment : false}
                            color="primary"
                            onChange={(event) => {
                                if (site !== undefined) {
                                    setSite({
                                        ...site,
                                        siteConfiguration: {
                                            ...site.siteConfiguration,
                                            automaticSessionAbandonment: event.target.checked,
                                        }
                                    });
                                }
                            }}
                        />
                    </FormField>
                )}

                {(isGlobalAdmin || isDealerAdmin) && (
                    <FormField label="Site Notes">
                        <FormControl>
                            <TextField
                                value={site ? site.notes || "" : ""}
                                onChange={(event) => {
                                    if (site !== undefined) {
                                        setSite({ ...site, notes: event.currentTarget.value });
                                    }
                                }}
                                InputProps={{
                                    inputProps: { className: classes.input },
                                }}
                            />
                        </FormControl>
                    </FormField>
                )}

                {site && isGlobalAdmin && (
                    <>
                        <FormField label="Maintenance Window">
                            <Checkbox
                                checked={site ? Boolean(site.maintenanceWindow) : false}
                                color="primary"
                                onChange={(event) => {
                                    if (site !== undefined) {
                                        setSite({
                                            ...site, maintenanceWindow:
                                                event.target.checked
                                                    ? DEFAULT_MAINTENANCE_WINDOW
                                                    : null
                                        });
                                    }
                                }}
                            />
                        </FormField>
                        <MaintenanceWindowConfiguration
                            configuration={site.maintenanceWindow || EMPTY_MAINTENANCE_WINDOW}
                            setConfiguration={(conf) => setSite({ ...site, maintenanceWindow: conf })}
                            disabled={!site.maintenanceWindow}
                        />
                    </>
                )}

                <div style={{ display: 'flex', gap: '10px' }}>
                    <Button color="primary" variant="contained" onClick={updateSite}>
                        Save
                    </Button>
                </div>
                <div>{validationErrors ? <ValidationErrors errors={validationErrors} /> : null}</div>

                {site && (
                    <DeviceActionsSection
                        site={site}
                        showDangerousButtons={showDeviceActionsSection}
                        onEnableMaintenanceMode={() => setShowEnableMaintenanceDialog(true)}
                    />
                )}
                
                {site && <SitePhotos siteId={site.id} />}

            </ConfigurationContainer>

            {siteId && (
                <EnableMaintenanceModeDialog
                    open={showEnableMaintenanceDialog}
                    onClose={() => setShowEnableMaintenanceDialog(false)}
                    siteId={siteId}
                    onSuccess={handleMaintenanceModeSuccess}
                />
            )}
        </>
    );
};

const useStyles = makeStyles({
    input: {
        padding: "5px",
        width: "100%",
        maxWidth: "400px",
    },
});
